import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { INTERACTION_MODES_QUERY, InteractionModeOption, InteractionModesData, getOrderedModesData } from "./queries";

type UseInteractionModesResult = {
  modes: InteractionModeOption[];
  loading: boolean;
};

/**
 * Fetches the set of selectable CRM interaction modes from the backend via
 * GraphQL introspection (CRMInteractionModeEnum). The `description` on each
 * enum value is the human-facing label and is also used as the option value,
 * because `CRMInteraction.mode` is currently stored as the human label.
 *
 * Pass `defaultMode` to keep that option pinned at the top; remaining options
 * are returned alphabetically. When the query is still loading the `modes`
 * array is empty, which is safe for `Select` consumers.
 */
export default function useInteractionModes(defaultMode?: string | null, skip = false): UseInteractionModesResult {
  const { data, loading } = useQuery<InteractionModesData>(INTERACTION_MODES_QUERY, {
    fetchPolicy: "cache-first",
    skip: skip
  });

  const modes = useMemo<InteractionModeOption[]>(() => {
    const enumValues = data?.__type?.enumValues ?? [];

    const options = enumValues
      .map((enumValue) => enumValue.description)
      .filter((description): description is string => !!description && description.length > 0)
      .map((description) => ({ value: description, label: description }));

    return getOrderedModesData(options, defaultMode);
  }, [data, defaultMode]);

  return { modes, loading };
}
